import { ApiResponse, ChatHistoryEntry, Message } from './types'
import getConfig from 'next/config'
import React from 'react'
import { fetchWithTimeout } from '@/utils/fetchWithTimeout'

const {
  publicRuntimeConfig: { API_BASE_PATH },
} = getConfig()

export const CHATBOT_REQUEST_TIMEOUT_MS = 32000
export const CHATBOT_FEEDBACK_TIMEOUT_MS = 32000
const TIMEOUT_DEBUG_ENABLED = process.env.NEXT_PUBLIC_AI_TIMEOUT_DEBUG === 'true'

/** Extracts markdown citations: [File Name (Page X)](url) */
const citationRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g

/** Extracts page number from citation text, e.g. (Page 5) */
const pageNumberRegex = /\(Page\s+(\d+)\)/i
const isValidCitation = (citation: unknown): citation is { file_name: string; page?: number; url: string } => {
  if (typeof citation !== 'object' || citation === null) return false
  const obj = citation as Record<string, unknown>
  return (
    typeof obj.file_name === 'string' &&
    obj.file_name.trim().length > 0 &&
    (obj.page === undefined || (typeof obj.page === 'number' && obj.page > 0)) &&
    typeof obj.url === 'string' &&
    /^https?:\/\/.+/.test(obj.url)
  )
}

/** Extracts citations from response and returns cleaned content with citation metadata */
export const extractCitations = (
  response?: string
): { cleanedContent: string; citations: Array<{ file_name: string; page?: number; url: string }> } => {
  const text = response ?? ''
  const citations: Array<{ file_name: string; page?: number; url: string }> = []

  try {
    const matches = Array.from(text.matchAll(citationRegex))

    matches.forEach((match) => {
      try {
        const heading = match[1]
        const url = match[2]

        // Validate URL format
        if (!url || !/^https?:\/\/.+/.test(url)) {
          console.warn('[ChatbotUI] Skipping citation with invalid URL:', url)
          return
        }

        // Extract page number from heading (optional — not all citations include one)
        const pageMatch = heading.match(pageNumberRegex)
        let page: number | undefined
        if (pageMatch) {
          const parsedPage = parseInt(pageMatch[1], 10)
          if (isNaN(parsedPage) || parsedPage <= 0) {
            console.warn('[ChatbotUI] Invalid page number extracted:', pageMatch[1])
            return
          }
          page = parsedPage
        } else if (/\(Page\s+[^)]+\)/i.test(heading)) {
          // Heading has a (Page ...) pattern but no valid number — malformed, skip it
          console.warn('[ChatbotUI] Citation has malformed page reference:', heading)
          return
        }

        // Extract file name by removing page number if present
        const fileName = heading.replace(/\s*\(Page\s+\d+\)\s*$/i, '').trim()

        // Validate file name
        if (!fileName || fileName.length === 0) {
          console.warn('[ChatbotUI] Citation missing file name')
          return
        }

        const citation = { file_name: fileName, page, url }

        // Final validation before adding
        if (isValidCitation(citation)) {
          citations.push(citation)
        } else {
          console.warn('[ChatbotUI] Citation validation failed:', citation)
        }
      } catch (error) {
        console.error('[ChatbotUI] Error processing individual citation:', error, match)
      }
    })
  } catch (error) {
    console.error('[ChatbotUI] Error extracting citations from response:', error)
  }

  let cleanedContent = text.replace(citationRegex, '').trim()
  cleanedContent = cleanedContent.replace(/\n?\s*\*{0,2}Citations?\*{0,2}\s*\n?/gi, '\n')
  cleanedContent = cleanedContent.replace(/\n?\s*\*{0,2}Sources?\*{0,2}\s*\n?/gi, '\n')
  // Remove empty list items left behind after citation links are stripped out.
  // Matches lines that are just a dash with optional whitespace (mid-text or at end).
  cleanedContent = cleanedContent.replace(/\n\s*-\s*(?=\n|$)/g, '')
  cleanedContent = cleanedContent.replace(/\n{3,}/g, '\n\n').trim()

  return { cleanedContent, citations }
}

export interface FeedbackPayload {
  span_id: string
  label: 'thumbs-up' | 'thumbs-down'
  comment?: string
}

/** Submits feedback to the aiFeedback API */
export const submitFeedback = async (payload: FeedbackPayload): Promise<void> => {
  const response = await fetchWithTimeout(
    `${API_BASE_PATH}api/aiFeedback`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
    {
      timeoutMs: CHATBOT_FEEDBACK_TIMEOUT_MS,
      onTimeoutStart: () => {
        if (TIMEOUT_DEBUG_ENABLED) {
          console.debug('[ChatbotUI] Timeout reached for feedback API request; aborting', {
            timeoutMs: CHATBOT_FEEDBACK_TIMEOUT_MS,
            span_id: payload.span_id,
          })
        }
      },
      onTimeoutError: () => {
        console.warn('[ChatbotUI] Feedback request aborted after timeout', {
          timeoutMs: CHATBOT_FEEDBACK_TIMEOUT_MS,
          span_id: payload.span_id,
        })
      },
    }
  )

  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`)
  }
}

/** Sends query to chatbot API with language locale and prior chat history */
export const askChatbot = async (
  query: string,
  locale: 'en' | 'fr' = 'en',
  chatHistory: ChatHistoryEntry[] = []
): Promise<ApiResponse> => {
  const response = await fetchWithTimeout(
    `${API_BASE_PATH}api/annualReport`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        language: locale,
        chat_history: chatHistory,
      }),
    },
    {
      timeoutMs: CHATBOT_REQUEST_TIMEOUT_MS,
      onTimeoutStart: () => {
        if (TIMEOUT_DEBUG_ENABLED) {
          console.debug('[ChatbotUI] Timeout reached for annual report request; aborting', {
            timeoutMs: CHATBOT_REQUEST_TIMEOUT_MS,
          })
        }
      },
      onTimeoutError: () => {
        console.warn('[ChatbotUI] Annual report request aborted after timeout', {
          timeoutMs: CHATBOT_REQUEST_TIMEOUT_MS,
        })
      },
    }
  )

  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`)
  }

  return (await response.json()) as ApiResponse
}

export const createBotMessage = (data: ApiResponse, query: string, assistantTagLabel = 'AI Support Tool'): Message => {
  const { cleanedContent, citations } = extractCitations(data.response)

  return {
    id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
    type: 'Assistant',
    tag: assistantTagLabel,
    content: cleanedContent || 'No response received',
    timestamp: new Date(),
    span_id: data.agent_span_id ?? '',
    query,
    sources: citations.length > 0 ? citations : undefined,
  }
}

export const createErrorMessage = (assistantTagLabel = 'AI Support Tool', locale: 'en' | 'fr' = 'en'): Message => {
  const errorMessages = {
    en: "It looks like I'm having trouble retrieving that information right now. Could you please try your request one more time?",
    fr: "J'ai du mal à trouver cette information. Pourriez-vous reformuler votre question ?",
  }

  return {
    id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
    type: 'Assistant',
    tag: assistantTagLabel,
    content: errorMessages[locale],
    timestamp: new Date(),
    span_id: '',
    query: '',
  }
}

export const createUserMessage = (content: string, userTagLabel = 'You'): Message => ({
  id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
  type: 'Human',
  tag: userTagLabel,
  content,
  timestamp: new Date(),
})

/** Determines if the disclaimer ExpandCollapse should be visible */
export const shouldShowDisclaimer = (
  messages: Message[],
  isLoading: boolean,
  disclaimerTitle?: React.ReactNode,
  disclaimerBody?: React.ReactNode
): boolean => {
  const hasContent = (value?: React.ReactNode): boolean => {
    if (value === null || value === undefined || value === false) return false

    if (typeof value === 'string') return value.trim().length > 0

    if (Array.isArray(value)) return (value as React.ReactNode[]).some((item: React.ReactNode) => hasContent(item))

    return true
  }

  return (
    !isLoading &&
    messages.length > 0 &&
    messages.some((msg) => msg.type === 'Assistant') &&
    hasContent(disclaimerTitle) &&
    hasContent(disclaimerBody)
  )
}
